/**
 * PUCK-20: Keyboard shortcuts overlay — reference panel for all editor shortcuts.
 */

import { useEffect, useCallback, type CSSProperties } from "react";
import { Keyboard, X } from "lucide-react";

interface ShortcutDef {
  readonly keys: string;
  readonly description: string;
}

interface ShortcutGroup {
  readonly label: string;
  readonly shortcuts: readonly ShortcutDef[];
}

const SHORTCUT_GROUPS: readonly ShortcutGroup[] = [
  {
    label: "General",
    shortcuts: [
      { keys: "Cmd+Z", description: "Undo" },
      { keys: "Cmd+Shift+Z", description: "Redo" },
      { keys: "Cmd+S", description: "Save draft now" },
      { keys: "Cmd+P", description: "Open staging preview" },
      { keys: "Cmd+Shift+P", description: "Publish page" },
      { keys: "Cmd+/", description: "Show shortcuts" },
      { keys: "Escape", description: "Close open overlays" },
    ],
  },
  {
    label: "Components",
    shortcuts: [
      { keys: "Cmd+C", description: "Copy component" },
      { keys: "Cmd+V", description: "Paste component" },
      { keys: "Cmd+X", description: "Cut component" },
      { keys: "Cmd+D", description: "Duplicate component" },
      { keys: "Delete", description: "Delete component" },
      { keys: "Alt+ArrowUp", description: "Move component up" },
      { keys: "Alt+ArrowDown", description: "Move component down" },
      { keys: "Cmd+[", description: "Send component backward" },
      { keys: "Cmd+]", description: "Bring component forward" },
    ],
  },
  {
    label: "View",
    shortcuts: [
      { keys: "Alt+1", description: "Desktop view" },
      { keys: "Alt+2", description: "Tablet view" },
      { keys: "Alt+3", description: "Mobile view" },
      { keys: "Cmd+.", description: "Toggle layers panel" },
      { keys: "Cmd+Shift+C", description: "Open comments tab" },
      { keys: "Cmd+Shift+S", description: "Open style tab" },
    ],
  },
];

interface ShortcutsOverlayProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
}

const BACKDROP: CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.5)",
  zIndex: 1000,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const MODAL: CSSProperties = {
  background: "var(--color-card)",
  border: "1px solid var(--color-border)",
  borderRadius: "12px",
  width: "560px",
  maxHeight: "80vh",
  overflow: "auto",
  boxShadow: "0 20px 40px rgba(0,0,0,0.2)",
};

const HEADER: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "16px 20px",
  borderBottom: "1px solid var(--color-border)",
};

const TITLE: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  fontSize: "16px",
  fontWeight: 600,
  color: "var(--color-foreground)",
};

const CLOSE_BUTTON: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "32px",
  height: "32px",
  border: "none",
  borderRadius: "6px",
  background: "transparent",
  color: "var(--color-muted-foreground)",
  cursor: "pointer",
};

const GROUP_LABEL: CSSProperties = {
  padding: "12px 20px 4px",
  fontSize: "11px",
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  color: "var(--color-muted-foreground)",
};

const SHORTCUT_ROW: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "8px 20px",
};

const DESCRIPTION: CSSProperties = {
  fontSize: "13px",
  color: "var(--color-foreground)",
};

const KEYS: CSSProperties = {
  display: "flex",
  gap: "4px",
};

const KEY: CSSProperties = {
  padding: "2px 8px",
  fontSize: "12px",
  fontWeight: 500,
  fontFamily: "ui-monospace, 'SF Mono', Menlo, monospace",
  background: "var(--color-background)",
  border: "1px solid var(--color-border)",
  borderRadius: "4px",
  color: "var(--color-foreground)",
};

export function ShortcutsOverlay({ isOpen, onClose }: ShortcutsOverlayProps) {
  const handleEscape = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    },
    [onClose],
  );

  useEffect(() => {
    if (!isOpen) return;
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, handleEscape]);

  if (!isOpen) return null;

  return (
    <div style={BACKDROP} onClick={onClose} role="dialog" aria-label="Keyboard shortcuts">
      <div style={MODAL} onClick={(e) => e.stopPropagation()}>
        <div style={HEADER}>
          <div style={TITLE}>
            <Keyboard size={18} />
            Keyboard Shortcuts
          </div>
          <button type="button" style={CLOSE_BUTTON} onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        {SHORTCUT_GROUPS.map((group) => (
          <div key={group.label}>
            <div style={GROUP_LABEL}>{group.label}</div>
            {group.shortcuts.map((shortcut) => (
              <div key={shortcut.keys} style={SHORTCUT_ROW}>
                <span style={DESCRIPTION}>{shortcut.description}</span>
                <div style={KEYS}>
                  {shortcut.keys.split("+").map((key) => (
                    <span key={key} style={KEY}>
                      {key}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

